/**
 * Réglages de discipline par ligue (20261009190000_league_discipline_settings).
 *
 * Source de vérité : la RPC get_league_disciplinary_settings(p_league_id) —
 * réglages globaux (app_settings_versions.disciplinary_settings) avec, par-dessus,
 * league_settings_versions.config.discipline de la ligue. Ce module en est le
 * miroir côté webapp : il nettoie ce que l'écran écrit (mêmes clés, mêmes types
 * que la RPC retient) et normalise ce qui est lu, comme useAppSettings.
 */
import type { DisciplinarySettings } from "../../types";

/** Entiers >= 0 surchargeables par une ligue. */
export const LEAGUE_DISCIPLINE_INT_KEYS = [
  "yellowCardThreshold",
  "yellowCardSuspension",
  "directRedCardSuspension",
  "twoYellowsRedCardSuspension",
  "totalGameDays",
] as const;

export type LeagueDisciplineIntKey = (typeof LEAGUE_DISCIPLINE_INT_KEYS)[number];

export interface LeagueDisciplineOverrides {
  yellowCardThreshold?: number;
  yellowCardSuspension?: number;
  directRedCardSuspension?: number;
  twoYellowsRedCardSuspension?: number;
  totalGameDays?: number;
  enableMidSeasonReset?: boolean;
  midSeasonSplit?: "league" | "group";
  /**
   * Loi citée sous chaque titre de journée du PV (renderer : pvLawReferenceAr).
   * Pas un réglage de calcul : retenue à l'écriture par
   * save_league_discipline_settings (20261010020100), absente de
   * get_league_disciplinary_settings et donc de mergeLeagueDisciplinarySettings.
   */
  lawReferenceAr?: string;
}

/** Texte imprimé quand la ligue n'a pas de lawReferenceAr (= PV_LAW_REFERENCE_AR du renderer). */
export const DEFAULT_PV_LAW_REFERENCE_AR = "قانون كرة القدم للهواة (سبتمبر 2026)";

/** Longueur maximale retenue (même borne que la RPC). */
export const LAW_REFERENCE_MAX_LENGTH = 300;

/** Espaces normalisés comme la RPC : suites d'espaces / retours → un espace, bords retirés. */
export function normalizeLawReferenceAr(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const v = raw.replace(/\s+/g, " ").trim();
  return v.length >= 1 && v.length <= LAW_REFERENCE_MAX_LENGTH ? v : undefined;
}

export type LeagueDisciplineKey = keyof LeagueDisciplineOverrides;

export const LEAGUE_DISCIPLINE_KEYS: LeagueDisciplineKey[] = [
  ...LEAGUE_DISCIPLINE_INT_KEYS,
  "enableMidSeasonReset",
  "midSeasonSplit",
  "lawReferenceAr",
];

const MAX_INT = 1000;

/** Ne garde que les clés et types que save_league_discipline_settings retient (miroir exact du SQL). */
export function sanitizeLeagueDisciplineOverrides(raw: unknown): LeagueDisciplineOverrides {
  const out: LeagueDisciplineOverrides = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const src = raw as Record<string, unknown>;
  for (const key of LEAGUE_DISCIPLINE_INT_KEYS) {
    const v = src[key];
    if (typeof v === "number" && Number.isFinite(v) && Number.isInteger(v) && v >= 0 && v <= MAX_INT) {
      out[key] = v;
    }
  }
  if (typeof src.enableMidSeasonReset === "boolean") out.enableMidSeasonReset = src.enableMidSeasonReset;
  if (src.midSeasonSplit === "league" || src.midSeasonSplit === "group") out.midSeasonSplit = src.midSeasonSplit;
  const law = normalizeLawReferenceAr(src.lawReferenceAr);
  if (law !== undefined) out.lawReferenceAr = law;
  return out;
}

/** Les surcharges qui changent un calcul (tout sauf lawReferenceAr). */
export function computeOverrides(o: LeagueDisciplineOverrides): Omit<LeagueDisciplineOverrides, "lawReferenceAr"> {
  const { lawReferenceAr: _law, ...rest } = o;
  return rest;
}

/** Fusion à plat, la ligue l'emporte ; sans surcharge : l'objet global tel quel. */
export function mergeLeagueDisciplinarySettings<T extends Record<string, unknown> | null | undefined>(
  global: T,
  leagueDiscipline: unknown,
): T | (Record<string, unknown> & LeagueDisciplineOverrides) {
  // Miroir de la lecture : get_league_disciplinary_settings ne renvoie pas lawReferenceAr.
  const overrides = computeOverrides(sanitizeLeagueDisciplineOverrides(leagueDiscipline));
  if (Object.keys(overrides).length === 0) return global;
  return { ...(global ?? {}), ...overrides };
}

export const FALLBACK_DISCIPLINARY_SETTINGS: DisciplinarySettings = {
  yellowCardThreshold: 4,
  yellowCardSuspension: 1,
  directRedCardSuspension: 1,
  twoYellowsRedCardSuspension: 1,
  accumulationRules: [],
  sanctionReasons: [],
} as DisciplinarySettings;

/**
 * Normalisation d'un objet disciplinary_settings brut — celle que faisait
 * useAppSettings, inchangée ; midSeasonSplit n'est ajouté que s'il est présent
 * (les réglages globaux n'en ont pas : résultat identique à avant).
 */
export function normalizeDisciplinarySettings(raw: unknown): {
  settings: DisciplinarySettings;
  isFallback: boolean;
} {
  const d = raw as any;
  const has = !!d && typeof d === "object";
  if (!has) return { settings: FALLBACK_DISCIPLINARY_SETTINGS, isFallback: true };
  const settings: DisciplinarySettings = {
    yellowCardThreshold: d.yellowCardThreshold ?? FALLBACK_DISCIPLINARY_SETTINGS.yellowCardThreshold,
    yellowCardSuspension: d.yellowCardSuspension ?? FALLBACK_DISCIPLINARY_SETTINGS.yellowCardSuspension,
    directRedCardSuspension: d.directRedCardSuspension ?? FALLBACK_DISCIPLINARY_SETTINGS.directRedCardSuspension,
    twoYellowsRedCardSuspension: d.twoYellowsRedCardSuspension ?? FALLBACK_DISCIPLINARY_SETTINGS.twoYellowsRedCardSuspension,
    accumulationRules: d.accumulationRules || [],
    sanctionReasons: d.sanctionReasons || [],
    enableMidSeasonReset: d.enableMidSeasonReset ?? false,
    totalGameDays: d.totalGameDays ?? undefined,
  };
  if (d.midSeasonSplit === "league" || d.midSeasonSplit === "group") settings.midSeasonSplit = d.midSeasonSplit;
  return { settings, isFallback: false };
}

export const isGroupMidSeasonSplit = (settings: Partial<DisciplinarySettings> | null | undefined): boolean =>
  settings?.enableMidSeasonReset === true && settings?.midSeasonSplit === "group";
